import React, { useCallback, useEffect, useMemo } from 'react';

import useMetric from 'hooks/useMetric';
import useValueFormatters from 'hooks/useValueFormatters';

import { getDeltaPercent } from './compositeUtils';
import { formatPercent } from './NumberRatio';
import { PlaceholderMeasure, getValueKey } from './subtextPlaceholders';
import useStudioMetricValue from './useStudioMetricValue';

type OnValue = (args: { key: string; value: string }) => void;

// '…' while loading, '-' on error
const LOADING = '…';
const MISSING = '-';

// A change with its sign: "+1,234 mi", "-$12.50" (rather than the
// accountant's "($12.50)" a negative currency value formats as)
const useFormatDelta = () => {
  const { formatValue } = useValueFormatters();
  return useCallback(
    ({ metric, delta }: { metric: Metrics.Metric; delta: number }) => {
      const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';
      return `${sign}${formatValue({
        value: Math.abs(delta),
        formatting: metric.formatting,
      })}`;
    },
    [formatValue],
  );
};

// Fetches a metric's value over the period it's compared with and reports
// its change from the current value, and its % change: as the measures ask.
// Renders nothing.
const ComparisonReporter = ({
  metric,
  current,
  isCurrentLoading,
  isCurrentError,
  measures,
  comparison,
  onValue,
}: {
  metric: Metrics.Metric;
  current: number | undefined;
  isCurrentLoading: boolean;
  isCurrentError: boolean;
  measures: PlaceholderMeasure[];
  comparison: PersistedComparisonType;
  onValue: OnValue;
}) => {
  const previous = useStudioMetricValue({ metric, comparison });
  const formatDelta = useFormatDelta();

  const values = useMemo(
    () =>
      measures.map((measure) => {
        const key = getValueKey({ metricId: metric.id, measure });
        if (isCurrentLoading || previous.isLoading) {
          return { key, value: LOADING };
        }
        if (
          isCurrentError ||
          previous.isError ||
          current === undefined ||
          previous.value === undefined
        ) {
          return { key, value: MISSING };
        }
        if (measure === 'delta') {
          return {
            key,
            value: formatDelta({ metric, delta: current - previous.value }),
          };
        }
        const percent = getDeltaPercent({ current, previous: previous.value });
        return {
          key,
          value:
            percent === undefined
              ? MISSING
              : formatPercent({ value: percent, isSigned: true }),
        };
      }),
    [
      current,
      formatDelta,
      isCurrentError,
      isCurrentLoading,
      measures,
      metric,
      previous.isError,
      previous.isLoading,
      previous.value,
    ],
  );

  useEffect(() => {
    values.forEach(onValue);
  }, [onValue, values]);
  return null;
};

// Fetches one metric's value and reports it, formatted, and its change from
// the comparison period when asked for. Renders nothing.
const Reporter = ({
  metricId,
  measures,
  comparison,
  onValue,
}: {
  metricId: string;
  measures: PlaceholderMeasure[];
  comparison: PersistedComparisonType | undefined;
  onValue: OnValue;
}) => {
  const metric = useMetric(metricId);
  const { value, formattedValue, isLoading, isError } = useStudioMetricValue({
    metric,
  });
  const formatted = isLoading ? LOADING : isError ? MISSING : formattedValue;
  useEffect(() => {
    onValue({ key: getValueKey({ metricId }), value: formatted });
  }, [metricId, onValue, formatted]);

  if (!metric || measures.length === 0) {
    return null;
  }
  if (!comparison) {
    // Nothing to compare with, e.g. a report's comparison when it has none
    return (
      <>
        {measures.map((measure) => (
          <NoComparison
            key={measure}
            valueKey={getValueKey({ metricId, measure })}
            onValue={onValue}
          />
        ))}
      </>
    );
  }
  return (
    <ComparisonReporter
      metric={metric}
      current={value}
      isCurrentLoading={isLoading}
      isCurrentError={isError}
      measures={measures}
      comparison={comparison}
      onValue={onValue}
    />
  );
};

const NoComparison = ({
  valueKey,
  onValue,
}: {
  valueKey: string;
  onValue: OnValue;
}) => {
  useEffect(() => {
    onValue({ key: valueKey, value: MISSING });
  }, [onValue, valueKey]);
  return null;
};

// The formatted values of a changing list of metrics (hooks can't loop over
// one), over the dates and filters these sit in, and each one's change from
// the comparison period where its measures ask for it. Each is reported by
// its value key (getValueKey) as it arrives; '…' while loading, '-' on
// error or when there's nothing to compare with.
const MetricValueReporters = ({
  references,
  comparison,
  onValue,
}: {
  references: { metricId: string; measures: PlaceholderMeasure[] }[];
  // Keep stable: it keys the comparison's date scope
  comparison?: PersistedComparisonType;
  // Keep stable: a new function re-reports every value
  onValue: OnValue;
}) => (
  <>
    {references.map(({ metricId, measures }) => (
      <Reporter
        key={metricId}
        metricId={metricId}
        measures={measures}
        comparison={comparison}
        onValue={onValue}
      />
    ))}
  </>
);

export default MetricValueReporters;
